/**
 * What: Shared TestFeed credit-balance query for Shopify app surfaces.
 * Transport / data-flow: Calls the Shopify Worker `/api/testfeed-usage` proxy under the shop-scoped query key shared with plan-sync refetches.
 *
 */
import { useQuery } from '@tanstack/react-query'
import { shopifyApiFetch, type ShopifyAppBridge } from '../shopifyApiFetch'
import { shopifyQueryKey, useShopifyAppContext } from '../ShopifyAppContext'
import type { TestFeedUsageResponse } from '../typesUsage'
import { shopifyQueryPolicies } from '../queries/shopifyQueryPolicies'

async function loadTestFeedUsage(shopify: ShopifyAppBridge): Promise<TestFeedUsageResponse> {
  const response = await shopifyApiFetch(shopify, '/api/testfeed-usage')
  if (response.ok) return response.json()

  const payload = (await response.json().catch((error) => {
    console.error(error)
    return {}
  })) as { error?: unknown }
  const message =
    typeof payload.error === 'string' ? payload.error : `Available credits request failed (${response.status})`
  throw new Error(message)
}

export function testFeedUsageQueryKey(shopDomain: string, orgId: string | null, connectionId: string | null) {
  return shopifyQueryKey(shopDomain, orgId, connectionId, ['testfeed-usage'] as const)
}

/** Reads the server-derived merchant credit state; disabled until the TestFeed connection scope is verified. */
export function useTestFeedUsage(shopify: ShopifyAppBridge, options: { enabled?: boolean } = {}) {
  const { shopDomain, orgId, connectionId } = useShopifyAppContext()
  return useQuery({
    queryKey: testFeedUsageQueryKey(shopDomain, orgId, connectionId),
    queryFn: () => loadTestFeedUsage(shopify),
    ...shopifyQueryPolicies.usage,
    enabled: (options.enabled ?? true) && !!shopDomain && !!orgId && !!connectionId,
  })
}
